import { create } from 'zustand';
import { CustomGptFile } from '../../../features/chat/types/custom-gpt.types';
import { ASSISTANT_ICONS } from '../../../features/chat/constants/assistant-icons';
import { useModelStore } from 'state/store/chat/use-model-store';

interface CustomGptCreationState {
    // Basic Info
    name: string;
    description: string;
    systemPrompt: string;
    modelName: string;
    temperature: number;
    logoUrl?: string;
    isEditMode: boolean;

    // Creation progress
    activeStep: string; // 'configure' | 'knowledge-base' | 'sharing' | 'permission'
    customGptId: string | null;
    
    // Files
    files: CustomGptFile[];
    localFiles: File[]; // Files selected but not yet uploaded (if needed)

    // Actions
    setName: (name: string) => void;
    setDescription: (description: string) => void;
    setSystemPrompt: (prompt: string) => void;
    setModelName: (modelName: string) => void;
    setTemperature: (temp: number) => void;
    setLogoUrl: (url: string) => void;
    setActiveStep: (step: string) => void;
    setCustomGptId: (id: string | null) => void;
    setFiles: (files: CustomGptFile[]) => void;
    addLocalFile: (file: File) => void;
    removeLocalFile: (index: number) => void;
    removeFile: (fileId: string) => void;
    setInitialData: (data: {
        id: string;
        name: string;
        description: string;
        systemPrompt: string;
        modelName: string;
        temperature: number;
        logoUrl?: string;
        files?: CustomGptFile[];
    }) => void;
    reset: () => void;
}

export const useCustomGptStore = create<CustomGptCreationState>((set) => ({
    name: '',
    description: '',
    systemPrompt: '',
    modelName: useModelStore.getState().models.filter((model) => model.id !== 'auto')[0]?.id || '',
    temperature: 0.7,
    logoUrl: ASSISTANT_ICONS[0].id,
    isEditMode: false,
    activeStep: 'configure',
    customGptId: null,
    files: [],
    localFiles: [],

    setName: (name) => set({ name }),
    setDescription: (description) => set({ description }),
    setSystemPrompt: (systemPrompt) => set({ systemPrompt }),
    setModelName: (modelName) => set({ modelName }),
    setTemperature: (temperature) => set({ temperature }),
    setLogoUrl: (logoUrl) => set({ logoUrl }),
    setActiveStep: (activeStep) => set({ activeStep }),
    setCustomGptId: (customGptId) => set({ customGptId }),
    setFiles: (files) => set({ files }),
    addLocalFile: (file) => set((state) => ({ localFiles: [...state.localFiles, file] })),
    removeLocalFile: (index) => set((state) => ({ 
        localFiles: state.localFiles.filter((_, i) => i !== index) 
    })),
    removeFile: (fileId) => set((state) => ({
        files: state.files.filter((f) => f.fileId !== fileId)
    })),
    setInitialData: (data) => set({
        customGptId: data.id,
        name: data.name,
        description: data.description,
        systemPrompt: data.systemPrompt,
        modelName: data.modelName,
        temperature: data.temperature,
        logoUrl: data.logoUrl || ASSISTANT_ICONS[0].id,
        isEditMode: true,
        files: data.files || [],
        localFiles: [],
        activeStep: 'configure'
    }),
    reset: () => set({
        name: '',
        description: '',
        systemPrompt: '',
        modelName: useModelStore.getState().models.filter((model) => model.id !== 'auto')[0]?.id || '',
        temperature: 0.7,
        logoUrl: ASSISTANT_ICONS[0].id,
        isEditMode: false,
        activeStep: 'configure',
        customGptId: null,
        files: [],
        localFiles: [],
    }),
}));
